import React, { useState, useEffect, useCallback } from "react";
import { Pencil, ImagePlus, AlertCircle, Loader } from "lucide-react";

export default function LogoUploader({
  organization,
  onUpload // async function(file) => url
}) {
  const [previewSrc, setPreviewSrc] = useState(organization?.logo_image_file || null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Clean up object URL when unmounting or file changes
  useEffect(() => {
    return () => {
      if (previewSrc && previewSrc.startsWith("blob:")) {
        URL.revokeObjectURL(previewSrc);
      }
    };
  }, [previewSrc]);

  const handleImageChange = useCallback(
    async (e) => {
      const selected = e.target.files?.[0];
      if (!selected) return;
      if (!selected.type.startsWith("image/")) {
        setError("Please select a valid image file.");
        return;
      }
      setError("");
      setFile(selected);
      // show local preview
      setPreviewSrc(URL.createObjectURL(selected));

      // fire upload
      try {
        setLoading(true);
        const uploadedUrl = await onUpload(selected);
        setPreviewSrc(uploadedUrl);
      } catch (err) {
        console.error(err);
        setError("Upload failed. Try again.");
      } finally {
        setLoading(false);
      }
    },
    [onUpload]
  );

  return (
    <div className="relative flex flex-col items-center justify-center w-40 h-40 border border-gray-300 rounded-lg overflow-hidden">
      {/* Preview or placeholder */}
      {previewSrc ? (
        <img 
          src={previewSrc} 
          alt="Organization logo" 
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-full bg-gray-100 text-gray-400">
          <ImagePlus size={32} />
          <span className="mt-2 text-sm">Upload Logo</span>
        </div>
      )}

      {/* upload input */}
      <input
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        title="Upload logo"
      />
      
      {/* Pencil icon overlay */}
      <div className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-700" onClick={()=>{
        document.querySelector('input[type="file"]').click();
      }}>
        <Pencil size={16} className="text-gray-600" />
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
          <div className="flex flex-col items-center">
            <Loader size={24} className="animate-spin text-blue-500" />
            <span className="mt-2 text-sm text-gray-600">Uploading...</span>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="absolute bottom-0 w-full bg-red-100 p-2 text-xs text-red-700 flex items-center">
          <AlertCircle size={16} className="mr-1 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}